/**
 * Detection logic for RBAC permission-denial API errors — deliberately has
 * NO dependency on the Redux store (same reasoning as plan-lock-detection.util.ts:
 * api.service.ts is imported by nearly every service, including ones the
 * store's own slices pull in, so importing the store here would create a
 * circular module chain).
 *
 * Unlike plan-lock (opt-in: a call site must explicitly call
 * showPlanLockIfTagged to show the dialog), this one AUTO-DISPATCHES the
 * global PermissionLockDialog for every tagged error, via a plain
 * `window` CustomEvent rather than a direct Redux dispatch (which would need
 * the store import this file deliberately avoids) — AdminLayout.tsx (already
 * mounted on every admin page, already rendering PermissionLockDialog) is the
 * one place that listens and does the actual `store.dispatch(showPermissionLock(...))`
 * from a real component context.
 *
 * Why auto-dispatch instead of opt-in: a real production bug is what drove
 * this — a role denied on PUT /admin/alerts/:id/acknowledge got a 403 with
 * zero UI feedback at all, because that page's catch block had simply never
 * been wired up to the opt-in mechanism. An opt-in pattern is only as
 * reliable as every future page remembering to adopt it; auto-dispatch makes
 * "the user sees SOMETHING when denied" the default, not an opt-in nobody
 * has to remember. The trade-off this creates — background/polling requests
 * would otherwise pop the modal on every failed poll cycle — is handled by
 * `suppressPermissionLock` in the request config (see permission-lock.util.ts's
 * header for the fuller reasoning and admin.api.ts for where it's applied).
 *
 * See permission-lock.util.ts for the store-dispatching half of this
 * (the listener side + a few remaining opt-in helpers for call sites that
 * still want an explicit, synchronous "was this shown" check).
 */

// Module augmentation, not a separate interface — this makes
// `{ suppressPermissionLock: true }` a structurally valid AxiosRequestConfig
// everywhere in the app (admin.api.ts passes it straight through to
// api.get/post/put/etc's `config` parameter), and makes `error.config` carry
// it back out on the way to the interceptor's check, with no `as any` casts
// needed on either side.
declare module 'axios' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface AxiosRequestConfig {
    /** See admin.api.ts's RequestOptions doc comment. */
    suppressPermissionLock?: boolean;
  }
}

const INSUFFICIENT_PERMISSIONS_CODE = 'INSUFFICIENT_PERMISSIONS';

/** Fired on `window` when a tagged, non-suppressed permission error is seen. AdminLayout.tsx listens. */
export const PERMISSION_DENIED_EVENT = 'api:permission-denied';

export interface ApiErrorBody {
  code?: string;
  message?: string;
}

export interface PermissionLockPayload {
  title: string;
  message: string;
}

/**
 * Unlike FORBIDDEN (thrown from ~14+ unrelated places — tenant-state,
 * identity mismatch, structural safeguards, plan-tier), INSUFFICIENT_PERMISSIONS
 * is cleanly scoped on the backend to exactly two things: requirePermission's
 * RBAC-tier denial and the legacy requireRole/requireTenantAdmin/
 * requireTenantOwner role-string check (auth.middleware.ts) — both genuinely
 * "your role doesn't grant this," never a resource-protection or
 * tenant-state reason. No message-marker needed to disambiguate, unlike
 * isPlanLockError's FORBIDDEN handling.
 */
export function isPermissionLockError(code?: string): boolean {
  return code === INSUFFICIENT_PERMISSIONS_CODE;
}

export function buildPermissionLockPayload(message: string): PermissionLockPayload {
  return {
    title: 'Access restricted',
    message,
  };
}

/**
 * Called from the axios response interceptor. Tags the error (so any catch
 * block can still check `error.permissionLock` synchronously) AND fires the
 * global auto-dispatch window event — unless this specific request opted out
 * via `suppressPermissionLock` in its axios config (background/polling
 * requests where a denial should degrade quietly, not interrupt the user;
 * see admin.api.ts). Tagging itself is unconditional and cheap — even a
 * suppressed request's catch block can still tell "this was specifically a
 * permission issue" for its own (non-modal) messaging.
 */
export function tagAxiosErrorWithPermissionLock(error: any): void {
  const apiError: ApiErrorBody | undefined = error?.response?.data?.error;
  if (apiError && isPermissionLockError(apiError.code) && apiError.message) {
    error.permissionLock = buildPermissionLockPayload(apiError.message);
    if (!error.config?.suppressPermissionLock && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PERMISSION_DENIED_EVENT, { detail: error.permissionLock }));
    }
  }
}
